import React, { useMemo } from 'react';

/**
 * Generate a unique ID for SVG gradient elements
 * Uses crypto.randomUUID() if available, otherwise falls back to Math.random()
 */
const generateUniqueId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID().replace(/-/g, '');
  }
  // Fallback for environments without crypto.randomUUID
  return `svg${Math.random().toString(36).substring(2, 15)}${Math.random().toString(36).substring(2, 15)}`;
};

export interface BannerSVGProps {
  /** Primary fill color - accepts hex color or CSS variable */
  primaryFillHexColor?: string;
  /** SVG viewBox attribute */
  viewBox?: string;
  /** SVG preserveAspectRatio attribute */
  preserveAspectRatio?: string;
}

/**
 * BannerSVG Component
 * 
 * Decorative wave/gradient SVG background used in OC chat widget.
 * Ported from ocfe library with no external dependencies.
 * 
 * Features:
 * - Multiple gradient layers with blend modes
 * - Unique IDs to prevent conflicts with multiple instances
 * - Configurable color via primaryFillHexColor
 * - Responsive via viewBox and preserveAspectRatio
 * 
 * @example
 * ```tsx
 * <BannerSVG 
 *   primaryFillHexColor="#007bff"
 *   viewBox="0 0 1600 250"
 * />
 * ```
 */
export const BannerSVG: React.FC<BannerSVGProps> = ({
  primaryFillHexColor = '#007bff',
  viewBox = '0 0 1600 250',
  preserveAspectRatio = 'xMidYMid slice',
}) => {
  // Generate unique ID once per component instance
  const uniqueId = useMemo(() => generateUniqueId(), []);

  const svgElement = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      viewBox={viewBox}
      preserveAspectRatio={preserveAspectRatio}
      aria-hidden="true"
      style={{ width: '100%', height: '100%', display: 'block' }}
    >
      <defs>
        {/* Base gradient - grayscale wave pattern */}
        <linearGradient
          id={`${uniqueId}a`}
          x1="399.6"
          x2="1238.185"
          y1="-398.455"
          y2="440.13"
          gradientTransform="matrix(1 0 0 -1 0 252)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#9fa2a4" />
          <stop offset="1" stopColor="#dcddde" />
        </linearGradient>

        {/* Gradient variants referencing base gradient */}
        <linearGradient
          id={`${uniqueId}b`}
          x1="-8455.753"
          x2="-5370.533"
          y1="-1501.49"
          y2="1583.73"
          gradientTransform="matrix(-.264 0 0 -1 -1028.524 252)"
          xlinkHref={`#${uniqueId}a`}
        />
        <linearGradient
          id={`${uniqueId}c`}
          x1="390.247"
          x2="1197.197"
          y1="-389.102"
          y2="417.848"
          xlinkHref={`#${uniqueId}a`}
        />
        <linearGradient
          id={`${uniqueId}d`}
          x1="399.6"
          x2="1246.556"
          y1="-398.455"
          y2="448.501"
          xlinkHref={`#${uniqueId}a`}
        />
        <linearGradient
          id={`${uniqueId}e`}
          x1="-10482.125"
          x2="-7325.674"
          y1="-1392.28"
          y2="1764.172"
          gradientTransform="matrix(-.264 0 0 -1 -1550.139 311.401)"
          xlinkHref={`#${uniqueId}a`}
        />
        <linearGradient
          id={`${uniqueId}f`}
          x1="2590.443"
          x2="5029.843"
          y1="-1082.229"
          y2="1357.171"
          gradientTransform="matrix(.339 0 0 -1 -489.358 311.401)"
          xlinkHref={`#${uniqueId}a`}
        />

        {/* Clip path */}
        <clipPath id={`${uniqueId}g`}>
          <path fill="none" d="M-1.2 0H1598.8V250H-1.2z" />
        </clipPath>
      </defs>

      {/* Wave layers with blend modes */}
      <g style={{ isolation: 'isolate' }}>
        {/* Base color fill */}
        <path 
          fill={primaryFillHexColor || '#F58023'} 
          d="M-0 0H1600V250H-0z" 
        />

        {/* Wave layer 1 */}
        <path
          fill={`url(#${uniqueId}a)`}
          fillRule="evenodd"
          d="M-.4 250s157.2-125.2 321.9-125 217.6 87.3 488.1 87.3 408-149.6 565.9-149.6 224.1 118.4 224.1 118.4v68.9z"
          style={{ mixBlendMode: 'multiply' }}
        />

        {/* Wave layer 2 */}
        <path
          fill={`url(#${uniqueId}b)`}
          fillRule="evenodd"
          d="M1601.2 205.755s-157.2-125.2-321.9-125-217.6 87.3-488.1 87.3-408-149.5-565.9-149.5-224.1 118.3-224.1 118.3l-1.6 113.6h1600z"
          opacity="0.43"
          style={{ mixBlendMode: 'multiply', isolation: 'isolate' }}
        />

        {/* Wave layer 3 */}
        <path
          fill={`url(#${uniqueId}c)`}
          fillRule="evenodd"
          d="M-.2 212.755s162.4-169.7 496-149.6c282.8 17 373.6 129.5 566.1 140.7 192.4 11.2 531.8 26.8 531.8 26.8l6 19.8H-.4z"
          opacity="0.4"
          style={{ mixBlendMode: 'multiply', isolation: 'isolate' }}
        />

        {/* Wave layer 4 */}
        <path
          fill={`url(#${uniqueId}d)`}
          fillRule="evenodd"
          d="M-.4 250s176.8-94.5 537.2-94.5 363.8 74.6 525 74.6 218-203.1 356.4-203.1 181.4 223 181.4 223H-.4z"
          opacity="0.4"
          style={{ mixBlendMode: 'multiply', isolation: 'isolate' }}
        />

        {/* Wave layer 5 */}
        <path
          fill={`url(#${uniqueId}e)`}
          fillRule="evenodd"
          d="M1600.4 116.955l-.8-116.5c-17.382 0-372.332-3.194-388.112 1.777C1153.205 20.59 1016.513 118 770.388 116.5 572.8 115.3 458.1 27.455 380.173-.555L-.4.455l.8 77.1-.8 172.9h1600z"
          opacity="0.43"
          style={{ mixBlendMode: 'multiply', isolation: 'isolate' }}
        />

        {/* Wave layer 6 */}
        <path
          fill={`url(#${uniqueId}f)`}
          fillRule="evenodd"
          d="M.5 116.955s156.8-71.6 321.1-71.5 168.6 70.758 438.5 70.758S1215.5 9.955 1373 9.955s223.6 67.7 223.6 67.7l.8 172.9H1.3z"
          opacity="0.43"
          style={{ mixBlendMode: 'multiply', isolation: 'isolate' }}
        />
      </g>
    </svg>
  );

return svgElement;
};

export default React.memo(BannerSVG);

